import { useCallback, useRef } from 'react';

import { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { ApiJobFunction } from 'api/types/ApiJobFunction';
import { ApiSynonymMini } from 'api/types/ApiSynonymMini';
import { H2 } from 'components/Text/H2';
import { TileCarousel } from 'components/TileCarousel/TileCarousel';
import { useFetchFn } from 'hooks/useFetchFn';
import { useElementReachedEffect } from 'hooks/viewport/useElementReachedEffect';
import { searchAlgoliaFetchSimilarJobsExperiment } from 'modules/search/algolia/fetch/similarJobs/searchAlgoliaFetchSimilarJobsExperiment';
import { searchGetJobsRoutePath } from 'modules/search/routing/helpers/searchGetJobsRoutePath';
import { useSearchLocation } from 'modules/search/zustand-stores/searchStore';
import { trackEventOnce } from 'utils/analytics/track/trackEventOnce';

import { ViewJobPageRecommendationsHolder } from './ViewJobPageRecommendations.styled';

type Props = {
  functions: ApiJobFunction[];
  jobId: string;
  jobTitle: string;
  synonym: ApiSynonymMini | null;
  orgAreasOfFocus?: ApiAreaOfFocus[];
};

export function ViewJobPageRecommendationsExperimentInternal({
  jobId,
  jobTitle,
  synonym,
  functions,
  orgAreasOfFocus,
}: Props) {
  const userSearchLocation = useSearchLocation();

  const relatedJobsRef = useRef<HTMLDivElement>(null);

  const [similarJobsResponse] = useFetchFn({
    fn: searchAlgoliaFetchSimilarJobsExperiment,
    args: [jobTitle, synonym, functions, userSearchLocation, orgAreasOfFocus],
  });

  useElementReachedEffect({
    ref: relatedJobsRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      if (Array.isArray(similarJobsResponse?.hits)) {
        trackEventOnce('Displayed: Featured Listings', {
          carousel_location: 'Job Listing',
          listing_type: 'JOB',
          listing_count: similarJobsResponse?.hits.length,
          layout: 'REGULAR',
          recommended_listings: similarJobsResponse?.hits.length
            ? similarJobsResponse.hits.map((hit) => hit.objectID)
            : undefined,
        });
      }
    }, [similarJobsResponse?.hits]),
  });

  return (
    <div ref={relatedJobsRef}>
      <H2 textAlign="center">{getText('Similar Jobs')}</H2>
      <TileCarousel
        headline=""
        results={similarJobsResponse?.hits || []}
        seeAll={{
          /* TRANSLATORS: Link to job search */
          text: getText('See all jobs'),
          action: {
            type: 'link',
            url: searchGetJobsRoutePath({ lang: CURRENT_LOCALE }),
          },
        }}
        listingType="JOB"
        carouselLocation="Job Listing"
        fromListingId={jobId}
      />
    </div>
  );
}

export function ViewJobPageRecommendationsExperiment({
  jobId,
  jobTitle,
  synonym,
  functions,
  orgAreasOfFocus,
}: Props) {
  return (
    <ViewJobPageRecommendationsHolder data-qa-id="recommended-jobs-section">
      <ViewJobPageRecommendationsExperimentInternal
        jobId={jobId}
        jobTitle={jobTitle}
        synonym={synonym}
        functions={functions}
        orgAreasOfFocus={orgAreasOfFocus}
      />
    </ViewJobPageRecommendationsHolder>
  );
}
